import { useState, useCallback } from 'react';

/**
 * Custom hook for managing async operations with pending, error, and data states
 * @param {Function} asyncFn - The async function to execute
 * @param {boolean} autoExecute - Whether to execute the function on mount (default: false)
 * @returns {Object} - { data, isPending, error, execute, reset }
 */
const useAsync = (asyncFn, autoExecute = false) => {
    const [data, setData] = useState(null);
    const [isPending, setIsPending] = useState(autoExecute);
    const [error, setError] = useState(null);

    const execute = useCallback(
        async (...args) => {
            setIsPending(true);
            setError(null);
            setData(null);

            try {
                const result = await asyncFn(...args);
                setData(result);
                return result;
            } catch (err) {
                setError(err);
                throw err;
            } finally {
                setIsPending(false);
            }
        },
        [asyncFn]
    );

    const reset = useCallback(() => {
        setData(null);
        setError(null);
        setIsPending(false);
    }, []);

    return { data, isPending, error, execute, reset };
};

export default useAsync;
